import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function SiteHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Home', isRoute: true },
    { to: '/about', label: 'About', isRoute: true },
    { to: '/programs', label: 'Packages', isRoute: true },
    { to: '/blog', label: 'Blog', isRoute: true },
    { to: isHome ? '#testimonials' : '/#testimonials', label: 'Success Stories', isRoute: false },
  ];

  const transparent = isHome && !isScrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'shadow-lg' : ''}`}
        style={{ background: transparent ? 'transparent' : '#ffffff', borderBottom: transparent ? 'none' : '2px solid #B8860B' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/73df10f1d2f13e308059e9f42ef7d721/1a1b91da26efffeb07731f8f238e690f.png"
                alt="Limitless Legacy Fitness Logo"
                className="w-14 h-14 object-contain flex-shrink-0"
              />
              <span
                className="font-bold whitespace-nowrap text-sm sm:text-base"
                style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
              >
                <span className="hidden sm:inline">Limitless Legacy Fitness and Health INC.</span>
                <span className="sm:hidden">Limitless Legacy</span>
              </span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-5">
              {navLinks.map(link =>
                link.isRoute ? (
                  <Link
                    key={link.to}
                    to={link.to}
                    className="text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    style={{
                      color: location.pathname === link.to
                        ? '#B8860B'
                        : transparent ? '#F5D76E' : '#333333',
                      borderBottom: location.pathname === link.to ? '2px solid #B8860B' : '2px solid transparent',
                      paddingBottom: '2px',
                    }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#B8860B')}
                    onMouseLeave={e => (e.currentTarget.style.color = location.pathname === link.to ? '#B8860B' : transparent ? '#F5D76E' : '#333333')}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <a
                    key={link.to}
                    href={link.to}
                    className="text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer"
                    style={{ color: transparent ? '#F5D76E' : '#333333' }}
                    onMouseEnter={e => (e.currentTarget.style.color = '#B8860B')}
                    onMouseLeave={e => (e.currentTarget.style.color = transparent ? '#F5D76E' : '#333333')}
                  >
                    {link.label}
                  </a>
                )
              )}
              <a
                href={isHome ? '#booking' : '/#booking'}
                className="px-5 py-2 text-sm font-bold rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer hover:opacity-90"
                style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000000' }}
              >
                Book a Session
              </a>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer"
              style={{ color: transparent ? '#F5D76E' : '#B8860B' }}
              onClick={() => setMenuOpen(prev => !prev)}
              aria-label="Toggle menu"
            >
              <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div
            className="md:hidden px-4 pb-5 pt-2 flex flex-col gap-2"
            style={{ background: '#ffffff', borderTop: '1px solid #f0e0b0' }}
          >
            {navLinks.map(link =>
              link.isRoute ? (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-semibold py-2.5 px-3 rounded-md cursor-pointer"
                  style={{ color: location.pathname === link.to ? '#B8860B' : '#333333', background: location.pathname === link.to ? 'rgba(184,134,11,0.08)' : 'transparent' }}
                >
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.to}
                  href={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="text-sm font-semibold py-2.5 px-3 rounded-md cursor-pointer"
                  style={{ color: '#333333' }}
                >
                  {link.label}
                </a>
              )
            )}
            <a
              href={isHome ? '#booking' : '/#booking'}
              onClick={() => setMenuOpen(false)}
              className="text-sm font-bold py-3 px-3 rounded-md text-center cursor-pointer mt-1"
              style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000000' }}
            >
              Book a Session
            </a>
          </div>
        )}
      </header>

      {/* Sticky Mobile CTA */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 px-4 py-3"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)', borderTop: '1px solid #B8860B' }}
      >
        <a
          href={isHome ? '#booking' : '/#booking'}
          className="block w-full text-center py-3 rounded-md font-bold text-sm whitespace-nowrap cursor-pointer"
          style={{ background: 'linear-gradient(90deg, #B8860B, #F5D76E)', color: '#000000' }}
        >
          Book a Free Consultation
        </a>
      </div>
    </>
  );
}
